import { useApp } from '@/App';
import FleetTitle from '@/libs/antd/components/Title';
import fetcher, { FleetPagination } from '@/libs/fleet-api/fetcher';
import {
	GetSCRisalahParam,
	getSCRisalah,
	SCRisalah,
} from '@/libs/fleet-api/services/nap';
import { ArrowRightOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { FC, useState, useMemo } from 'react';
import { MasterData } from '@/libs/fleet-api/services/master-data';

const FSCustomerListPage: FC = () => {
	const { navigate } = useApp();
	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<GetSCRisalahParam>({
		search: undefined,
		filter: undefined,
		pagination,
	});
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getSCRisalah', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getSCRisalah(queryParams);
		},
	});

	const dataSource = useMemo(() => data?.data.items ?? [], [data?.data.items]);

	// Type for processed data with rowspan properties
	type ProcessedSCRisalah = SCRisalah & {
		key: number;
		originalIndex: number;
		noRowSpan: number;
		napNoRowSpan: number;
		spfNoRowSpan: number;
		customerRowSpan: number;
		customerGroupRowSpan: number;
		frmRowSpan: number;
		fbdhRowSpan: number;
		divisionRowSpan: number;
		viewResolutionDocumentRowSpan: number;
		inputResolutionRowSpan: number;
		lastModifiedByRowSpan: number;
	};

	// Process data for rowspan functionality
	const processedData = useMemo(() => {
		const rawData = dataSource;
		if (!rawData.length) return [];

		const groupedByNAPId: Record<
			string,
			(SCRisalah & { originalIndex: number })[]
		> = {};
		const result: ProcessedSCRisalah[] = [];

		// Group by napNo
		rawData.forEach((item, index) => {
			const napNo = item.napNo;
			if (!groupedByNAPId[napNo]) {
				groupedByNAPId[napNo] = [];
			}
			groupedByNAPId[napNo].push({ ...item, originalIndex: index });
		});

		// Process groups and add rowspan information
		Object.values(groupedByNAPId).forEach((group) => {
			group.forEach((item, groupIndex) => {
				const groupLength = group.length;
				const isFirstInGroup = groupIndex === 0;

				result.push({
					...item,
					key: item.originalIndex,
					// Add rowspan properties for columns that should be merged
					noRowSpan: isFirstInGroup ? groupLength : 0,
					napNoRowSpan: isFirstInGroup ? groupLength : 0,
					spfNoRowSpan: isFirstInGroup ? groupLength : 0,
					customerRowSpan: isFirstInGroup ? groupLength : 0,
					customerGroupRowSpan: isFirstInGroup ? groupLength : 0,
					frmRowSpan: isFirstInGroup ? groupLength : 0,
					fbdhRowSpan: isFirstInGroup ? groupLength : 0,
					divisionRowSpan: isFirstInGroup ? groupLength : 0,
					viewResolutionDocumentRowSpan: isFirstInGroup ? groupLength : 0,
					inputResolutionRowSpan: isFirstInGroup ? groupLength : 0,
					lastModifiedByRowSpan: isFirstInGroup ? groupLength : 0,
				});
			});
		});

		// Sort back to original order
		return result.sort((a, b) => a.originalIndex - b.originalIndex);
	}, [dataSource]);

	// Create custom numbering for No column that works with rowspan
	const getRowNumber = (record: ProcessedSCRisalah) => {
		const currentPage = pagination.pageNumber;
		const pageSize = pagination.pageSize;
		const baseNumber = (currentPage - 1) * pageSize;
		return baseNumber + record.originalIndex + 1;
	};

	// Debug: Log the first item to check if spfNo and lastModifiedAt are present
	if (dataSource.length > 0) {
		console.log('Risalah data sample:', {
			spfNo: dataSource[0].spfNo,
			lastModifiedAt: dataSource[0].lastModifiedAt,
			lastModifiedBy: dataSource[0].lastModifiedBy,
			fullRecord: dataSource[0],
		});
	}

	const searchOptionQuery = useQuery({
		queryKey: ['/naps/resolutions/get-search-options'] as const,
		queryFn: async ({ queryKey }) => {
			const [api] = queryKey;
			const res = await fetcher<{
				searchByOptions: string[];
				divisionOptions: MasterData[];
				plafonTypeOptions: MasterData[];
				financingLimitType: MasterData[];
				frmOptions: MasterData[];
				fbdhOptions: MasterData[];
			}>({
				api,
			});

			return res.data;
		},
	});

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>List Debitur Risalah</FleetTitle>

			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					console.log(values);
					setQueryParams((params) => ({
						...params,
						frmId: values.frmId,
						fbdhId: values.fbdhId,
						plafonTypeId: values.plafonTypeId,
						divisionId: values.divisionId,
						financingLimitTypeId: values.financingLimitTypeId,
						search: values.searchValue
							? {
									by: values.searchBy,
									value: values.searchValue,
								}
							: undefined,
					}));
				}}
			>
				<Form.Item
					noStyle
					shouldUpdate
				>
					{({ getFieldValue }) => {
						const searchBy = getFieldValue('searchBy') as string;

						const renderSearch = () => {
							if (searchBy === 'frmId') {
								return (
									<Form.Item name={'frmId'}>
										<Select
											options={
												searchOptionQuery.data?.frmOptions?.map((option) => {
													return {
														value: option.id,
														label: option.value,
													};
												}) ?? []
											}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'fbdhId') {
								return (
									<Form.Item name={'fbdhId'}>
										<Select
											options={
												searchOptionQuery.data?.fbdhOptions?.map((option) => {
													return {
														value: option.id,
														label: option.value,
													};
												}) ?? []
											}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'divisionId') {
								return (
									<Form.Item name={'divisionId'}>
										<Select
											options={
												searchOptionQuery.data?.divisionOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'plafonTypeId') {
								return (
									<Form.Item name={'plafonTypeId'}>
										<Select
											options={
												searchOptionQuery.data?.plafonTypeOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'financingLimitTypeId') {
								return (
									<Form.Item name={'financingLimitTypeId'}>
										<Select
											options={
												searchOptionQuery.data?.financingLimitType?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								);
							}

							return (
								<Form.Item name="searchValue">
									<Input
										placeholder="Search..."
										style={{
											display: 'block',
										}}
									/>
								</Form.Item>
							);
						};
						return (
							<Row gutter={[20, 20]}>
								<Col flex="none">
									<Form.Item
										name="searchBy"
										label={<span>Search By</span>}
										labelCol={{ flex: 'auto', style: { width: 150 } }}
										wrapperCol={{ flex: 'none', style: { width: 250 } }}
									>
										<Select
											placeholder="Search By"
											options={[
												{
													label: 'Nama Customer',
													value: 'customer-name',
												},
												{
													label: 'Group BMPP',
													value: 'group-name',
												},
												{
													label: 'FRM',
													value: 'frmId',
												},
												{
													label: 'FBDH',
													value: 'fbdhId',
												},
												{
													label: 'Divisi',
													value: 'divisionId',
												},
												{
													label: 'Jenis Plafond',
													value: 'plafonTypeId',
												},
												{
													label: 'Sifat Limit',
													value: 'financingLimitTypeId',
												},
											]}
											allowClear
											style={{
												display: 'block',
												width: '100%',
											}}
										/>
									</Form.Item>
								</Col>
								<Col flex="auto">{renderSearch()}</Col>
								<Col>
									<Form.Item>
										<Button
											block
											type="primary"
											htmlType="submit"
											disabled={!searchBy}
										>
											SEARCH
										</Button>
									</Form.Item>
								</Col>
							</Row>
						);
					}}
				</Form.Item>
			</Form>

			<Table
				bordered
				loading={isLoading || isFetching}
				scroll={{ x: 'max-content' }}
				dataSource={processedData}
				columns={[
					{
						title: 'No',
						width: 1,
						render: (_, record) => getRowNumber(record),
						onCell: (record) => ({
							rowSpan: record.noRowSpan,
						}),
					},
					{
						title: 'NAP ID',
						render: (_, record) => record.napNo,
						onCell: (record) => ({
							rowSpan: record.napNoRowSpan,
						}),
					},
					{
						title: 'No SPF',
						render: (_, record) => {
							console.log('Rendering SPF No for record:', {
								spfNo: record.spfNo,
								napNo: record.napNo,
							});
							return record.spfNo ?? '-';
						},
						onCell: (record) => ({
							rowSpan: record.spfNoRowSpan,
						}),
					},
					{
						title: 'Nama Customer',
						render: (_, record) => record.customer.value,
						onCell: (record) => ({
							rowSpan: record.customerRowSpan,
						}),
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.customerGroup.value,
						onCell: (record) => ({
							rowSpan: record.customerGroupRowSpan,
						}),
					},
					{
						title: 'FRM',
						render: (_, record) => record.frm.value,
						onCell: (record) => ({
							rowSpan: record.frmRowSpan,
						}),
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdh.value,
						onCell: (record) => ({
							rowSpan: record.fbdhRowSpan,
						}),
					},
					{
						title: 'Divisi',
						render: (_, record) => record.division.value,
						onCell: (record) => ({
							rowSpan: record.divisionRowSpan,
						}),
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType.value,
					},
					{
						title: 'Sifat Limit',
						render: (_, record) => record.financingLimitType.value,
					},
					{
						title: 'View Dokumen Risalah',
						width: 1,
						align: 'center',
						render: (_, record) =>
							!record.inputResolution ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										navigate('SCRisalahPage', {
											prospectId: record.prospectId,
											isReadOnly: true,
										});
									}}
								>
									<FileSearchOutlined />
								</a>
							) : (
								<FileSearchOutlined style={{ color: '#BABABA' }} />
							),
						onCell: (record) => ({
							rowSpan: record.viewResolutionDocumentRowSpan,
						}),
					},
					{
						fixed: 'right',
						title: 'Input Risalah',
						align: 'center',
						width: 1,
						render: (_, record) => {
							if (!record.inputResolution) {
								return <ArrowRightOutlined style={{ color: '#BABABA' }} />;
							}
							return (
								<a
									style={{ display: 'block' }}
									onClick={(e) => {
										e.preventDefault();
										navigate('SCRisalahPage', {
											prospectId: record.prospectId,
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
						onCell: (record) => ({
							rowSpan: record.inputResolutionRowSpan,
						}),
					},
					{
						fixed: 'right',
						title: 'Last Modified Risalah',
						align: 'center',
						render: (_, record) => {
							return `${record.lastModifiedBy ?? '-'}`;
						},
						onCell: (record) => ({
							rowSpan: record.lastModifiedByRowSpan,
						}),
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => {
							return {
								pageSize,
								pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
							};
						});
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
			/>
		</Space>
	);
};

export default FSCustomerListPage;
